import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { API } from "../App";

function PreviewTable({ rows, onOverride, phaseKey, overrideActions = null, session = null }) {
  const [sortCol, setSortCol] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [localOverrides, setLocalOverrides] = useState({});
  const globalOverrides = (session?.overrides && phaseKey) ? (session.overrides[phaseKey] || []) : [];

  useEffect(() => {
    // Clear optimistic local overrides whenever the server state (globalOverrides) changes.
    // This ensures that when a phase is re-run and server overrides are dropped, 
    // the UI correctly un-greys the rows instead of getting stuck in local state.
    setLocalOverrides({});
  }, [globalOverrides.length]);
  
  if (!Array.isArray(rows) || rows.length === 0)
    return <p className="text-sm text-muted-foreground">No rows.</p>;

  // Extremely robust filter to prevent ANY crashes from malformed data
  const safeRows = rows.filter(r => r && typeof r === 'object');
  if (safeRows.length === 0)
    return <p className="text-sm text-muted-foreground">No valid rows to display.</p>;

  const activeRows = safeRows;
  
  if (activeRows.length === 0)
    return <p className="text-sm text-muted-foreground">All rows resolved or ignored.</p>;

  const cols = Object.keys(activeRows[0] || safeRows[0] || {});

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortAsc(!sortAsc);
    } else {
      setSortCol(col);
      setSortAsc(true);
    }
  };

  
  const isNumericColumn = (colName) => {
    const lower = colName.toLowerCase();
    return lower.includes("amount") || lower.includes("balance") || lower.includes("tax") || 
           lower.includes("commission") || lower.includes("fee") || lower.includes("rate") || 
           lower.includes("price") || lower.includes("total") || lower.includes("revenue");
  };

  const toggleSelectAll = () => {
    const allValidIds = sortedRows.map(r => r.booking_id || r.ota_booking_id || r.pnr || r.sf_booking_id || (r.ota_pnrs && r.ota_pnrs[0]) || (r.sf_booking_ids && r.sf_booking_ids[0])).filter(Boolean);
    if (selectedIds.size === allValidIds.length && allValidIds.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allValidIds));
    }
  };

  const toggleRow = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleBatchIgnore = (actionType = "ignored") => {
    if (selectedIds.size > 0 && onOverride) {
      const newLocal = { ...localOverrides };
      selectedIds.forEach(id => newLocal[id] = true);
      setLocalOverrides(newLocal);
      
      onOverride(phaseKey, Array.from(selectedIds), actionType);
      setSelectedIds(new Set());
    }
  };

  const sortedRows = [...activeRows].sort((a, b) => {
    if (!sortCol) return 0;
    let vA = a[sortCol];
    let vB = b[sortCol];
    if (vA === null || vA === undefined) vA = "";
    if (vB === null || vB === undefined) vB = "";
    if (typeof vA === "number" && typeof vB === "number") {
      return sortAsc ? vA - vB : vB - vA;
    }
    vA = String(vA).toLowerCase();
    vB = String(vB).toLowerCase();
    if (vA < vB) return sortAsc ? -1 : 1;
    if (vA > vB) return sortAsc ? 1 : -1;
    return 0;
  });

  const getStickyStyle = (index, isHeader) => {
    let offset = onOverride ? 44 : 0;
    // Only stick the first 2 columns, forcing strict widths and truncation to prevent overlapping
    if (index === 0) return { position: 'sticky', left: offset, zIndex: isHeader ? 20 : 10, width: 240, minWidth: 240, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' };
    if (index === 1) return { position: 'sticky', left: offset + 240, zIndex: isHeader ? 20 : 10, width: 160, minWidth: 160, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' };
    return {};
  };

  const getStickyClass = (index) => {
    const isLastSticky = (index === 1) || (index === 0 && cols.length === 1);
    if (isLastSticky) return "bg-white dark:bg-slate-950 shadow-[1px_0_0_0_#e2e8f0] dark:shadow-[1px_0_0_0_#1e293b]";
    if (index < 2) return "bg-white dark:bg-slate-950";
    return "";
  };

  const getValidExportRows = () => {
    return activeRows.filter(r => {
      const identifier = r.booking_id || r.ota_booking_id || r.pnr || r.sf_booking_id || (r.ota_pnrs && r.ota_pnrs[0]) || (r.sf_booking_ids && r.sf_booking_ids[0]);
      return !(identifier && (localOverrides[identifier] || globalOverrides.includes(String(identifier)) || globalOverrides.includes(identifier)));
    });
  };

  const exportToCSV = () => {
    const rowsToExport = getValidExportRows();
    if (rowsToExport.length === 0) {
      toast.error("No valid rows to export");
      return;
    }
    const header = cols.join(",");
    const csvRows = rowsToExport.map(row => 
      cols.map(c => {
        let v = row[c];
        if (v === null || v === undefined) return "";
        v = String(v).replace(/"/g, '""');
        return `"${v}"`;
      }).join(",")
    );
    const csvContent = [header, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${phaseKey || 'export'}_table.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded CSV");
  };

  const copyToClipboard = async () => {
    const rowsToExport = getValidExportRows();
    if (rowsToExport.length === 0) {
      toast.error("No valid rows to export");
      return;
    }
    const header = cols.join("\t");
    const tsvRows = rowsToExport.map(row => 
      cols.map(c => {
        let v = row[c];
        return (v === null || v === undefined) ? "" : String(v).replace(/\t/g, " ").replace(/\n/g, " ");
      }).join("\t")
    );
    const tsvContent = [header, ...tsvRows].join("\n");
    try {
      await navigator.clipboard.writeText(tsvContent);
      toast.success("Copied to clipboard!");
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-end gap-2 mb-1">
        <Button size="sm" variant="outline" className="h-8 text-slate-600" onClick={copyToClipboard}>
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
          Copy Data
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-slate-600" onClick={exportToCSV}>
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
          Download CSV
        </Button>
        {onOverride && selectedIds.size > 0 && (
          <>
            <div className="w-px h-8 bg-slate-200 mx-1"></div>
            {overrideActions ? (
              overrideActions.map(action => (
                <Button 
                  key={action.type} 
                  size="sm" 
                  variant={action.variant || "outline"} 
                  className={action.className || "h-8 text-green-600 border-green-200 hover:bg-green-50"} 
                  onClick={() => handleBatchIgnore(action.type)}
                >
                  {action.label} ({selectedIds.size})
                </Button>
              ))
            ) : (
              <Button size="sm" variant="outline" className="h-8 text-green-600 border-green-200 hover:bg-green-50" onClick={() => handleBatchIgnore()}>
                ✓ Ignore Selected ({selectedIds.size})
              </Button>
            )}
          </>
        )}
      </div>
      <div className="overflow-auto border rounded-md max-h-[600px] relative">
        <Table className="mb-6">
          <TableHeader>
            <TableRow>
              {onOverride && (
                <TableHead className="w-[44px] min-w-[44px] max-w-[44px] px-0 text-center sticky left-0 z-20 bg-white dark:bg-slate-950 shadow-[1px_0_0_0_#e2e8f0] dark:shadow-[1px_0_0_0_#1e293b]" style={{ padding: 0 }}>
                  <input type="checkbox" checked={selectedIds.size === sortedRows.length && sortedRows.length > 0} onChange={toggleSelectAll} className="cursor-pointer" />
                </TableHead>
              )}
            {cols.map((c, idx) => (
              <TableHead 
                key={c} 
                className={`whitespace-nowrap text-xs cursor-pointer select-none hover:bg-muted/50 transition-colors group ${getStickyClass(idx)} ${isNumericColumn(c) ? "text-right" : ""}`}
                style={getStickyStyle(idx, true)}
                onClick={() => handleSort(c)}
              >
                <div className={`flex items-center gap-1 ${isNumericColumn(c) ? "justify-end" : ""}`}>
                  {c}
                  {sortCol === c ? (
                    <span className="text-muted-foreground text-[10px] leading-none">{sortAsc ? "▲" : "▼"}</span>
                  ) : (
                    <span className="text-muted-foreground/30 text-[10px] leading-none opacity-0 group-hover:opacity-100">↕</span>
                  )}
                </div>
              </TableHead>
            ))}

          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRows.map((r, i) => {
            const identifier = r.booking_id || r.ota_booking_id || r.pnr || r.sf_booking_id || (r.ota_pnrs && r.ota_pnrs[0]) || (r.sf_booking_ids && r.sf_booking_ids[0]);
            const isOverridden = identifier && (localOverrides[identifier] || globalOverrides.includes(String(identifier)) || globalOverrides.includes(identifier));
            
            return (
              <TableRow key={i} className={isOverridden ? "opacity-30 grayscale pointer-events-none transition-all duration-300 bg-slate-50" : "transition-all duration-300"}>
                {onOverride && (
                  <TableCell className="tabular-nums w-[44px] min-w-[44px] max-w-[44px] px-0 text-center sticky left-0 z-10 bg-white dark:bg-slate-950 shadow-[1px_0_0_0_#e2e8f0] dark:shadow-[1px_0_0_0_#1e293b]" style={{ padding: 0 }}>
                    {identifier && (
                      <input type="checkbox" checked={selectedIds.has(identifier)} onChange={() => toggleRow(identifier)} className="cursor-pointer" />
                    )}
                  </TableCell>
                )}
                {cols.map((c, idx) => {
                  let v = r?.[c];
                  if (v === null || v === undefined) v = "";
                  else if (typeof v === "boolean") v = v ? "Yes" : "No";
                  else if (Array.isArray(v)) v = v.join(", ");
                  else v = String(v);
                  return (
                    <TableCell key={c} 
                      className={`tabular-nums text-xs whitespace-nowrap ${getStickyClass(idx)} ${isNumericColumn(c) ? "text-right" : ""}`}
                      style={getStickyStyle(idx, false)}
                    >
                      {v}
                    </TableCell>
                  );
                })}

              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </div>
    </div>
  );
}


export default PreviewTable;
